import React, { useEffect, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Container, makeStyles, Grid, Typography, CardContent, Card, useTheme,
} from '@material-ui/core';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  PieChart, Pie, Cell,
} from 'recharts';

import {
  selectStatistics, loadStatistics, loadVideoAnalytics, setFilters,
} from '../../store/slices/statisticsSlice';
import { Auth0Context, IS_ADMIN_KEY } from '../../utils/auth0';
import AdminFiltersControl from '../../components/AdminFiltersControl';
import LoadingErrorContainer from '../../components/LoadingErrorContainer';
import VideoAnalyticsTable from './VideoAnalyticsTable';
import PromisScore from '../../components/PromisScore';
import InfoPopover from '../../components/InfoPopover';
import PromisPainScale from '../../assets/PromisPainScale';
import PromisFunctionScale from '../../assets/PromisFunctionScale';
import PromisScalePoint from '../../components/PromisScalePoint';

export default function StatisticsScreen() {
  const classes = useStyles();
  const theme = useTheme();
  const { user, getAccessTokenSilently } = useAuth0() as Auth0Context;
  const isAdmin = user[IS_ADMIN_KEY];

  const dispatch = useDispatch();
  const {
    filters, statistics, videoAnalytics, loading, error,
  } = useSelector(selectStatistics);

  useEffect(() => {
    dispatch(loadStatistics(getAccessTokenSilently));
  }, [filters, getAccessTokenSilently]);
  useEffect(() => {
    if (isAdmin) {
      dispatch(loadVideoAnalytics(getAccessTokenSilently));
    }
  }, [user, filters, getAccessTokenSilently]);

  return (
    <Container className={classes.container}>
      {isAdmin && (
        <AdminFiltersControl
          filters={filters}
          onChange={(filters) => dispatch(setFilters(filters))}
          classes={{ container: classes.filtersControl }}
        />
      )}
      <LoadingErrorContainer
        loading={loading}
        error={error && 'An error has occured.'}
      >
        {statistics != null && (
          <>
            <Grid container spacing={2} alignItems="stretch">
              <GeneralStatistic
                title="Total Patients"
                value={statistics.totalUsers}
              />
              <GeneralStatistic
                title="Total Sessions Completed"
                value={statistics.totalSessions}
              />
              <GeneralStatistic
                title="Average BMI"
                value={statistics.meanBmi}
              />
              <GeneralStatistic
                title="Average Age"
                value={statistics.meanAge}
              />
              <GeneralStatistic
                title="Average PROMIS® Pain Score Change"
                value={statistics.meanDifferencePromisPain}
              >
                <PromisScore
                  score={statistics.meanDifferencePromisPain}
                  change
                  type="pain"
                />
                <InfoPopover>
                  <Typography>
                    The minimal clinical important difference (MCID value) is a
                    2 point reduction in pain.
                  </Typography>
                </InfoPopover>
              </GeneralStatistic>
              <GeneralStatistic
                title="Average PROMIS® Function Score Change"
                value={statistics.meanDifferencePromisFunction}
              >
                <PromisScore
                  score={statistics.meanDifferencePromisFunction}
                  change
                  type="function"
                />
                <InfoPopover>
                  <Typography>
                    The minimal clinical important difference (MCID value) is a
                    2.4 point increase in function.
                  </Typography>
                </InfoPopover>
              </GeneralStatistic>
              <GeneralStatistic
                title="Average Baseline PROMIS® Pain Score"
                value={statistics.meanBaselinePromisPain}
              >
                <PromisScore
                  score={statistics.meanBaselinePromisPain}
                  type="pain"
                  showCategory
                  classes={{
                    scoreIcon: classes.promisScoreIcon,
                    category: classes.promisCategory,
                  }}
                />
                <InfoPopover>
                  <PromisPainScale className={classes.promisScale}>
                    <PromisScalePoint
                      score={statistics.meanBaselinePromisPain}
                    />
                  </PromisPainScale>
                </InfoPopover>
              </GeneralStatistic>
              <GeneralStatistic
                title="Average Baseline PROMIS® Function Score"
                value={statistics.meanBaselinePromisFunction}
              >
                <PromisScore
                  score={statistics.meanBaselinePromisFunction}
                  type="function"
                  showCategory
                  classes={{
                    scoreIcon: classes.promisScoreIcon,
                    category: classes.promisCategory,
                  }}
                />
                <InfoPopover>
                  <PromisFunctionScale className={classes.promisScale}>
                    <PromisScalePoint
                      score={statistics.meanBaselinePromisFunction}
                    />
                  </PromisFunctionScale>
                </InfoPopover>
              </GeneralStatistic>
              <CountsStatistic
                title="Pathology"
                counts={statistics.programCounts}
              />
              <CountsStatistic
                title="Risk Category at Baseline"
                counts={statistics.riskCategoryCounts}
                colorMap={{
                  Low: theme.palette.success.main,
                  Moderate: theme.palette.warning.main,
                  High: theme.palette.error.main,
                  Exclude: theme.palette.primary.main,
                }}
              />
              <CountsStatistic
                title="Sex"
                counts={statistics.sexCounts}
              />
              {user[IS_ADMIN_KEY] && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        className={classes.statisticTitle}
                      >
                        Video Analytics
                      </Typography>
                      <VideoAnalyticsTable />
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </LoadingErrorContainer>
    </Container>
  );
}

function GeneralStatistic(props: {
  title: string,
  value: number,
  children?: ReactNode,
}) {
  const {
    title, value, children,
  } = props;

  const classes = useStyles();
  const theme = useTheme();

  const valueFormat = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
  });

  return (
    <Grid item xs={6} md={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" className={classes.statisticTitle}>
            {title}
          </Typography>
          <Typography className={classes.statisticValue}>
            {children || valueFormat.format(value)}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

function CountsStatistic(props: {
  title: string,
  counts: { [label: string]: number },
  colorMap?: { [label: string]: string},
}) {
  const { title, counts, colorMap } = props;
  const data = Object.keys(counts).map((label) => ({
    name: label,
    value: counts[label],
  }));
  data.sort((a, b) => b.value - a.value);

  const classes = useStyles();
  const theme = useTheme();

  let colors = [
    theme.palette.info.main,
    theme.palette.error.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.primary.main,
    theme.palette.secondary.main,
  ];
  if (colorMap != null) {
    colors = data.map((datum) => colorMap[datum.name]);
  }

  const RADIAN = Math.PI / 180;
  const CustomLabel = (props: {
    fill: string, name: string, cx: number, x: number, y: number,
  }) => {
    const {
      name, fill, cx, x, y,
    } = props;
    return (
      <text
        x={x > cx ? x + 4 : x - 4}
        y={y}
        fill={fill}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontFamily={theme.typography.body1.fontFamily}
      >
        {name}
      </text>
    );
  };

  return (
    <Grid item xs={12} md={4}>
      <Card>
        <CardContent>
          <Typography variant="h6" className={classes.statisticTitle}>
            {title}
          </Typography>
          <PieChart width={300} height={250} className={classes.pieChart}>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              label={CustomLabel as any}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              isAnimationActive={false}
            >
              {data.map(({ name }, index) => (
                <Cell key={name} fill={colors[index % colors.length]} />
              ))}
            </Pie>
          </PieChart>
        </CardContent>
      </Card>
    </Grid>
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3) + 64,
    paddingBottom: theme.spacing(3),
  },
  filtersControl: {
    paddingBottom: theme.spacing(2),
  },
  statisticTitle: {
    lineHeight: 1.25,
    fontSize: 18,
  },
  statisticValue: {
    fontSize: 36,
    fontWeight: 300,
    textAlign: 'center',
  },
  promisScale: {
    width: 400,
  },
  promisScoreIcon: {
    top: -3,
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  promisCategory: {
    fontSize: 14,
    position: 'relative',
    fontWeight: 'normal',
    marginLeft: theme.spacing(0.5),
    top: -9,
  },
  pieChart: {
    marginLeft: 'auto',
    marginRight: 'auto',
    '& svg': {
      overflow: 'visible',
    },
  },
}));
